import { useEffect, useState } from "react";

import {
    scrollElementIntoViewOnKeyboardVisible,
    adjustContentIntoView,
    restoreContentIntoView,
    getContentNode,
    getDataKeyboardScrollNode,
} from "../utils";

declare global {
    interface Window {
        keyboardStateChanged: (visible: boolean, keyboardHeight: number) => void;
    }
}

const BOTTOM_OFFSET = 24;
/** Default textarea `name` from BottomInputHalfCardContent used by Cooking Request / SpecialInstructions. */
const COOKING_REQUEST_INPUT_NAME = "BOTTOM_INPUT_HALF_CARD_CONTENT";

const useKeyboardState = (): { isKeyboardOpen: boolean } => {
    const [isKeyboardOpen, setKeyboardOpen] = useState(false);

    useEffect(() => {
        let timer: number | null = null;
        let container: HTMLElement | null = null;
        let cookingRequestDialog: HTMLElement | null = null;
        let canAdjustHeight = false;
        const oldFn = window.keyboardStateChanged;

        const clearTimer = (): void => {
            if (timer) window.cancelAnimationFrame(timer);
        };

        const clearCookingRequestLift = (): void => {
            cookingRequestDialog?.style.removeProperty("transform");
            cookingRequestDialog = null;
        };

        const reset = (containerVal: HTMLElement | null): void => {
            document.documentElement.style.removeProperty("overflow-y");
            document.body.style.transform = "translateY(0)";
            document.documentElement.style.removeProperty("touch-action");
            // Clear the published keyboard height so CSS consumers (bottom-sheet lift via
            // --ios-keyboard-height) collapse back to 0 when the keyboard closes.
            document.documentElement.style.removeProperty("--ios-keyboard-height");
            clearCookingRequestLift();
            setKeyboardOpen(false);
            clearTimer();
            restoreContentIntoView(containerVal);
            canAdjustHeight = true;
        };

        if (typeof oldFn === "function") {
            oldFn(false, 0);
        }

        window.keyboardStateChanged = (visible: boolean, keyboardHeight: number): void => {
            /**
             * if keyboard is visible,
             *  => scroll the focused element into view if required
             *  => change content height w.r.t to keyboard
             * else
             *  => restore content height to full view avaialble
             */
            if (visible) {
                setKeyboardOpen(true);
                const focusedElement = document.activeElement;

                if (!focusedElement) {
                    return;
                }

                const parentElement = getDataKeyboardScrollNode(focusedElement);
                const isCookingRequest = focusedElement.getAttribute("name") === COOKING_REQUEST_INPUT_NAME;

                if (keyboardHeight > 0) {
                    // used for suggestive typing component
                    document.documentElement.style.setProperty("--ios-keyboard-height", `${keyboardHeight}`);
                    document.documentElement.style.setProperty("overflow-y", "hidden");
                    document.documentElement.style.setProperty("touch-action", "none");
                }

                if (isCookingRequest) {
                    clearTimer();
                    timer = window.requestAnimationFrame(() => {
                        // Lift only the half-card (not body) so the page behind stays put.
                        // Use transform instead of padding so the sheet does not grow/become scrollable.
                        cookingRequestDialog = focusedElement.closest("[role=dialog]") as HTMLElement | null;
                        if (cookingRequestDialog) {
                            cookingRequestDialog.style.transform = `translateY(-${keyboardHeight}px)`;
                        }
                    });
                    return;
                }

                const rect = focusedElement.getBoundingClientRect();

                // take existing container instance; when switching from one focused to another focused
                const newContainer = getContentNode(focusedElement, keyboardHeight);
                if (newContainer !== container) {
                    reset(container);
                }

                container = newContainer;

                clearTimer();
                timer = window.requestAnimationFrame(() => {
                    if (parentElement) {
                        document.body.style.transform = `translateY(-${keyboardHeight}px)`;
                    }
                    if (canAdjustHeight) {
                        adjustContentIntoView(container, keyboardHeight);
                    }
                    canAdjustHeight = false;
                    scrollElementIntoViewOnKeyboardVisible({
                        container,
                        rect,
                        bottomOffset: BOTTOM_OFFSET,
                    });
                });
            } else {
                setKeyboardOpen(false);
                reset(container);
            }
        };

        return (): void => {
            clearTimer();
            clearCookingRequestLift();
            window.keyboardStateChanged = oldFn;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isKeyboardOpen };
};

export { useKeyboardState };
